/* ==========================================================================
   Healeo native cart drawer
   Replaces the iCart app.

   Two rules shaped this file:

   1. All markup comes from Liquid via the Section Rendering API, never from
      string templates here. That is what keeps the drawer's numbers identical
      to the ones Shopify will charge: the discount that a merchant configures
      under Discounts > Automatic is already reflected in `cart.items[].
      final_line_price`, so rendering the real cart is the whole correctness
      story. iCart drew its own prices client-side, which is why its "FREE
      Melatonin" row never existed in /cart.js.

   2. The theme, and several apps, add to the cart through their own AJAX
      calls. Rather than guess at every button selector, we wrap fetch and
      XMLHttpRequest and react to any request that hits a /cart/ write
      endpoint. Anything that changes the cart therefore refreshes the drawer,
      including apps we do not control.
   ========================================================================== */
(function () {
  'use strict';

  var ROOT_ID = 'hn-cart-drawer';
  var SECTION_ID = 'hn-cart-drawer';
  var PAGE_ROOT_ID = 'hn-cart-page';
  var GIFT_PROP = '_hn_gift_tier';
  var WRITE_RE = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;
  var B2G2_PROBE_KEY = 'hn-b2g2-live';
  var B2G2_PROBE_TTL = 30 * 60 * 1000;

  var state = {
    root: null,
    open: false,
    busy: false,
    lastFocus: null,
    reconciling: false,
    suppress: 0,
    settings: {},
    booted: false,
    lastAdded: null,
    queue: null
  };

  /* ----------------------------------------------------------- utilities */

  function $(sel, ctx) { return (ctx || document).querySelector(sel); }
  function $$(sel, ctx) { return Array.prototype.slice.call((ctx || document).querySelectorAll(sel)); }

  function readSettings() {
    var el = document.getElementById('hn-cart-settings');
    if (!el) return {};
    try { return JSON.parse(el.textContent); } catch (e) { return {}; }
  }

  function routes() {
    var r = (window.Shopify && window.Shopify.routes) || {};
    var base = r.root || '/';
    if (base.slice(-1) !== '/') base += '/';
    return {
      cart: base + 'cart.js',
      add: base + 'cart/add.js',
      change: base + 'cart/change.js',
      base: base
    };
  }

  function postJSON(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    }).then(function (res) {
      return res.json().then(function (data) {
        if (!res.ok) throw Object.assign(new Error(data.description || res.statusText), { data: data });
        return data;
      });
    });
  }

  function getCart() {
    return fetch(routes().cart, { headers: { Accept: 'application/json' } }).then(function (r) { return r.json(); });
  }

  /* -------------------------------------------------------------- render */

  /* The /cart page's section id, read out of the DOM rather than guessed.

     The drawer is rendered from the layout, so its wrapper is the constant
     `shopify-section-hn-cart-drawer`. The page is a TEMPLATE section, so
     Shopify names its wrapper after the template — `template--1234__main` —
     and that number differs per theme and changes when a theme is duplicated.
     Hard-coding it would work here and break on the next clone; reading the
     wrapper the page actually rendered cannot go stale.

     Returns null off the /cart page, which is most of the site. */
  function pageSectionId() {
    var root = document.getElementById(PAGE_ROOT_ID);
    if (!root) return null;
    var host = root.closest('[id^="shopify-section-"]');
    return host ? host.id.replace('shopify-section-', '') : null;
  }

  function sectionIds() {
    var ids = [SECTION_ID];
    var page = pageSectionId();
    if (page) ids.push(page);
    return ids;
  }

  /* Shopify returns the whole `<div id="shopify-section-...">` for each id
     asked for; we swap only its children so the drawer element -- and its open
     state -- survive. Every id present in the response is applied, because on
     /cart both the drawer and the page render cart lines and updating one of
     them leaves the other showing the cart as it was before the click. */
  function applySections(sections) {
    if (!sections) return false;
    var wasOpen = state.open;
    var applied = false;
    Object.keys(sections).forEach(function (id) {
      var html = sections[id];
      if (typeof html !== 'string') return;
      var host = document.getElementById('shopify-section-' + id);
      if (!host) return;
      var doc = new DOMParser().parseFromString(html, 'text/html');
      var fresh = doc.getElementById('shopify-section-' + id) || doc.body.firstElementChild;
      if (!fresh) return;
      host.innerHTML = fresh.innerHTML;
      applied = true;
    });
    if (!applied) return false;
    bind();
    if (wasOpen) markOpen(true);
    return true;
  }

  function refresh() {
    return fetch(routes().base + '?sections=' + sectionIds().join(','), { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(applySections)
      .catch(function () { return false; });
  }

  function mutate(body) {
    setBusy(true);
    body.sections = sectionIds().join(',');
    body.sections_url = window.location.pathname + window.location.search;
    return postJSON(routes().change, body)
      .then(function (data) {
        applySections(data.sections);
        return reconcileAll(data);
      })
      .catch(function (err) {
        // A failed line change leaves the drawer showing stale numbers, so
        // always fall back to a full re-render rather than leaving it as-is.
        console.warn('[hn-cart]', err && err.message);
        return refresh();
      })
      .then(function () { setBusy(false); });
  }

  /* Every cart root, not the first one found. On /cart the page root is what
     `$('.hn-cart')` returns, so the drawer -- which is on that page too, and is
     what re-renders -- was never marked busy. */
  function setBusy(v) {
    state.busy = v;
    $$('.hn-cart').forEach(function (el) {
      el.setAttribute('data-busy', v ? 'true' : 'false');
    });
  }

  /* ---------------------------------------------------------- buy 2 get 2

     The store runs a site-wide "buy 2 get 2 free" for a few days each month.
     Shopify's automatic discount is a *pricing* rule: it never puts the free
     units in the basket. So shoppers add 2, are charged for 2, receive 2, and
     the ops team edits the order by hand. This raises the line to the whole
     block instead, and lets the merchant's own automatic discount price it.

     Three rules hold this together:

     1. A bump may never increase the bill. See b2g2Target().
     2. The free units are never priced here. Quantities are all this touches;
        the money comes from Discounts > Automatic, server-side, where it
        cannot be forged by anything running in the page.
     3. If that discount is not actually applying, every bump is rolled back
        and the feature disarms itself for the session. That is what makes the
        theme safe to leave switched on after a sale ends. */

  function b2g2Cfg() {
    var c = state.settings.b2g2 || {};
    var buy = parseInt(c.buy, 10) || 0;
    var get = parseInt(c.get, 10) || 0;
    var excluded = {};
    (c.exclude_ids || []).forEach(function (id) { excluded[String(id)] = true; });
    return {
      active: !!c.active && buy > 0 && get > 0,
      buy: buy,
      block: buy + get,
      excluded: excluded,
      notice: c.notice || '',
      expired_notice: c.expired_notice || ''
    };
  }

  /* Quantity the line should hold, given what the shopper chose.

     With a block of `buy + get`, a line of q units is charged for
         paid(q) = buy * floor(q / block) + (q mod block)
     and rounding up to the next whole block costs
         paid(next) - paid(q) = buy - (q mod block)

     So rounding up is free or cheaper exactly when the remainder is at least
     `buy`, and strictly more expensive below that. Bumping only on
     `remainder >= buy` is therefore a guarantee, not a heuristic: at 2 -> 4
     the shopper pays the same and receives double, at 3 -> 4 they pay less,
     and a lone unit (or 5, or 9) is left exactly as they set it. */
  function b2g2Target(q, cfg) {
    var r = q % cfg.block;
    if (r === 0 || r < cfg.buy) return q;
    return (Math.floor(q / cfg.block) + 1) * cfg.block;
  }

  /* Gifts, subscriptions and anything already free are left alone: a ₹0 gift
     line bumped to 4 hands out three more prizes, and a selling-plan line is
     not what the discount targets. */
  function b2g2Eligible(item, giftVariants, cfg) {
    if (!item.price) return false;
    if ((item.properties || {})[GIFT_PROP]) return false;
    if (giftVariants[String(item.variant_id)]) return false;
    if (item.selling_plan_allocation) return false;
    if (cfg.excluded[String(item.variant_id)]) return false;
    if (cfg.excluded[String(item.product_id)]) return false;
    return true;
  }

  /* What to change, given a cart.

     Once the discount applies, Shopify splits the variant into two cart lines:
     the half the shopper pays for, and the half that is free, same variant and
     same properties, told apart only by their discount allocation.

     They are one quantity as far as the shopper is concerned, and treating them
     as two independent lines is not a theoretical problem — a cart of 2 became
     a cart of 8 on the first real test, because each half was separately
     completed to a whole block. So quantities are summed per variant, the
     target is computed once from that sum, and the difference is added to the
     paid half. The free half is never written to; Shopify re-splits on its own
     when the total changes. */
  function b2g2Plan(cart, cfg, giftVariants) {
    var optedOut = b2g2OptOuts();
    var groups = {};

    /* Completion is PER PRODUCT, and only ever tops up a product the shopper
       actually chose.

       Doing it on the cart-wide total looked equivalent and is not: a basket
       holding one of two different things is two units short of a block, so the
       cart would add two more of one of them -- a product the shopper had asked
       for exactly one of. Add a third item afterwards and they are carrying a
       pair they never wanted and cannot get rid of without noticing.

       Per product, a lone unit of anything is left alone, and the case this
       feature exists for -- two of one thing, expecting four -- still works.
       Anything else is offered through the claim button, where the shopper
       decides. */
    cart.items.forEach(function (item) {
      if (!b2g2Eligible(item, giftVariants, cfg)) return;
      if (optedOut[String(item.variant_id)]) return;
      var vid = String(item.variant_id);
      var g = groups[vid] || (groups[vid] = { total: 0, paid: null, variant_id: item.variant_id });
      g.total += item.quantity;
      if (item.final_line_price > 0 && (!g.paid || item.quantity > g.paid.quantity)) {
        g.paid = item;
      }
    });

    var plan = [];
    Object.keys(groups).forEach(function (vid) {
      var g = groups[vid];
      // Nothing is being paid for on this variant, so there is no line that can
      // carry the extra units.
      if (!g.paid) return;
      var target = b2g2Target(g.total, cfg);
      if (target === g.total) return;
      plan.push({
        key: g.paid.key,
        variant_id: g.variant_id,
        from: g.paid.quantity,
        to: g.paid.quantity + (target - g.total),
        totalFrom: g.total,
        unitsGained: target - g.total
      });
    });
    return plan;
  }

  function b2g2OptOuts() {
    try { return JSON.parse(window.sessionStorage.getItem('hn-b2g2-optout')) || {}; }
    catch (e) { return {}; }
  }

  function b2g2SetOptOut(variantId, on) {
    var map = b2g2OptOuts();
    if (on) map[String(variantId)] = 1; else delete map[String(variantId)];
    try { window.sessionStorage.setItem('hn-b2g2-optout', JSON.stringify(map)); }
    catch (e) { /* private mode */ }
  }

  /* Every cart line holding this variant, gifts excluded. */
  function b2g2VariantLines(cart, variantId) {
    return cart.items.filter(function (item) {
      return String(item.variant_id) === String(variantId) && !(item.properties || {})[GIFT_PROP];
    });
  }

  /* Set the shopper's TOTAL for a variant, across both halves of a split.

     The free half is dropped first and the remainder written to the paid half,
     because Shopify owns the split: give it the right total on one line and it
     re-splits correctly. Writing to the free half directly just fights it. */
  /* Going UP is not an opt-out.

     Raising a quantity is the shopper reaching for more, which is exactly when
     the offer should apply -- 1 to 2 has to become 4 on its own, without them
     having to find a button. Only reducing or removing means "leave this
     alone", so only those set the opt-out; an increase lifts it and runs the
     completion straight after. */
  function b2g2StepTo(variantId, next, current) {
    var goingUp = next > current;
    if (goingUp) b2g2SetOptOut(variantId, false);
    return b2g2SetVariantTotal(variantId, next, !goingUp)
      .then(function () { return goingUp ? reconcileAll() : null; });
  }

  /* Write a variant's total, re-reading the cart between every step.

     A line key is not stable across a write here: removing one line, or merely
     changing how much of the discount lands on another, makes Shopify re-key
     what remains. A second write built from the keys of the first read lands on
     a line that no longer exists and fails silently.

     That is not a hypothetical -- it has now caused two separate bugs, one
     leaving units behind on delete and one leaving a rejected bump in place
     because the rollback could not find its own line. Every caller goes through
     here so it cannot happen a third time. */
  function b2g2WriteVariantTotal(variantId, total) {
    var steps = 0;

    function step() {
      return getCart().then(function (cart) {
        var lines = b2g2VariantLines(cart, variantId);
        if (!lines.length || ++steps > 8) return null;

        if (total <= 0) {
          return postJSON(routes().change, { id: lines[0].key, quantity: 0 }).then(step);
        }

        var paid = null;
        lines.forEach(function (l) {
          if (l.final_line_price > 0 && (!paid || l.quantity > paid.quantity)) paid = l;
        });
        if (!paid) paid = lines[0];

        // Collapse to one line first, so the number is written in one place and
        // Shopify re-splits it from there.
        var extra = lines.filter(function (l) { return l.key !== paid.key; });
        if (extra.length) {
          return postJSON(routes().change, { id: extra[0].key, quantity: 0 }).then(step);
        }

        var held = lines.reduce(function (n, l) { return n + l.quantity; }, 0);
        if (held === total) return null;
        // Deliberately the last write: Shopify splits the line again in
        // response, and going round once more would undo the split.
        return postJSON(routes().change, { id: paid.key, quantity: total });
      });
    }

    return step().catch(function (err) {
      console.warn('[hn-cart] write total', err && err.message);
    });
  }

  function b2g2SetVariantTotal(variantId, total, optOut) {
    b2g2SetOptOut(variantId, optOut !== false);
    setBusy(true);
    state.reconciling = true;
    return b2g2WriteVariantTotal(variantId, total)
      .then(function () { state.reconciling = false; return refresh(); })
      .then(function () { setBusy(false); });
  }

  /* "Claim Buy 2 Get 2" on a line the shopper has not taken the offer on.

     Not everyone wants four of something, so a single unit is left alone and
     offered the deal instead of having it applied to them. Taking it is an
     explicit opt-in, which is why it clears any earlier opt-out.

     It still has to prove the discount before it commits: the theme flag says
     the sale is meant to be on, not that Shopify agrees. If the discount total
     does not move, the extra units are taken straight back out rather than left
     for the shopper to pay full price for. */
  function b2g2Claim(variantId, target) {
    var cfg = b2g2Cfg();
    if (!cfg.active) return Promise.resolve(false);

    return getCart().then(function (cart) {
      var lines = b2g2VariantLines(cart, variantId);
      if (!lines.length) return false;
      var before = cart.total_discount || 0;
      var wasTotal = lines.reduce(function (n, l) { return n + l.quantity; }, 0);

      b2g2SetOptOut(variantId, false);
      return b2g2SetVariantTotal(variantId, target, false).then(function () {
        return getCart();
      }).then(function (after) {
        /* Only the discount is checked here, never the total.

           Claiming costs more on purpose -- the shopper is buying two units
           instead of one and getting two free on top. Applying the automatic
           rule's never-pay-more test to a button the shopper deliberately
           pressed made it revert every single time and report that the offer was
           not running, on a store where it was running perfectly. */
        if ((after.total_discount || 0) > before) {
          b2g2ProbeWrite(true);
          notice(cfg.notice || 'Buy 2 Get 2 applied — your free units were added. You pay for half.');
          return true;
        }
        b2g2ProbeWrite(false);
        return b2g2SetVariantTotal(variantId, wasTotal, true).then(function () {
          notice(cfg.expired_notice ||
                 'The Buy 2 Get 2 offer is not running right now, so your quantities were left as you set them.');
          return false;
        });
      });
    }).catch(function (err) {
      console.warn('[hn-cart] b2g2 claim', err && err.message);
      return refresh().then(function () { return false; });
    });
  }

  function b2g2ProbeRead() {
    try {
      var v = JSON.parse(window.sessionStorage.getItem(B2G2_PROBE_KEY));
      if (!v || Date.now() - v.t > B2G2_PROBE_TTL) return null;
      return !!v.live;
    } catch (e) { return null; }
  }

  function b2g2ProbeWrite(live) {
    try {
      window.sessionStorage.setItem(B2G2_PROBE_KEY, JSON.stringify({ live: !!live, t: Date.now() }));
    } catch (e) { /* private mode */ }
  }

  function reconcileB2G2(cartLike) {
    var cfg = b2g2Cfg();
    var idle = { bumped: 0, reverted: false };
    if (!cfg.active || state.reconciling) return Promise.resolve(idle);
    if (b2g2ProbeRead() === false) return Promise.resolve(idle);

    return (cartLike && cartLike.items ? Promise.resolve(cartLike) : getCart()).then(function (cart) {
      var giftVariants = {};
      tiers().forEach(function (t) { giftVariants[String(t.variant_id)] = true; });

      var plan = b2g2Plan(cart, cfg, giftVariants);
      if (!plan.length) return idle;

      var before = cart.total_discount || 0;
      /* The never-pay-more rule is proved on the real bill rather than on
         arithmetic. Across a mixed cart the discount frees the CHEAPEST units,
         so completing a basket of one dear item and one cheap one can raise the
         total even though the unit count says it should not. Whatever Shopify
         decides, if the shopper would pay more, the change is undone. */
      var beforePaid = cart.total_price || 0;
      var applied = [];
      state.reconciling = true;

      var chain = Promise.resolve();
      plan.forEach(function (p) {
        chain = chain.then(function () {
          return postJSON(routes().change, { id: p.key, quantity: p.to })
            .then(function () { applied.push(p); })
            // Sold out below the block, or a line something else removed while
            // we were working. Skip it; the rest of the cart still normalises.
            .catch(function (err) { console.warn('[hn-cart] b2g2 bump', err && err.message); });
        });
      });

      return chain.then(function () {
        if (!applied.length) {
          state.reconciling = false;
          return refresh().then(function () { return idle; });
        }
        return getCart().then(function (after) {
          var paidMore = (after.total_price || 0) > beforePaid;
          if ((after.total_discount || 0) > before && !paidMore) {
            b2g2ProbeWrite(true);
            state.reconciling = false;
            var added = applied.reduce(function (n, p) { return n + p.unitsGained; }, 0);
            return refresh().then(function () { return { bumped: added, reverted: false }; });
          }

          /* Either the discount is not covering this cart -- never created,
             expired, scoped elsewhere -- or it is, but this particular basket
             costs more once completed. Both end the same way: put every line
             back exactly as the shopper left it. */
          if (paidMore) {
            /* The discount is fine; this basket just costs more once completed,
               because the free units are the cheapest ones and completing moved
               which those are. Stand down for these products only -- retrying on
               every later cart change would churn the cart for nothing. The
               claim button still offers it, where the shopper can see the price
               and decide. */
            applied.forEach(function (p) { b2g2SetOptOut(p.variant_id, true); });
          } else {
            b2g2ProbeWrite(false);
          }
          var undo = Promise.resolve();
          applied.slice().reverse().forEach(function (p) {
            undo = undo.then(function () {
              return b2g2WriteVariantTotal(p.variant_id, p.totalFrom);
            });
          });
          return undo
            .then(function () { state.reconciling = false; return refresh(); })
            .then(function () { return { bumped: 0, reverted: true }; });
        });
      });
    }).catch(function (err) {
      state.reconciling = false;
      console.warn('[hn-cart] b2g2', err && err.message);
      return idle;
    });
  }

  /* While buy-2-get-2 is on, the gift ladder is stood down and any gift already
     in the basket is taken back out.

     The two offers cannot share a cart honestly. The ladder measures a subtotal
     that B2G2 is halving, so it reads as going backwards while the shopper adds
     more; and leaving the gift lines in place while snippets/hn-cart-body.liquid
     hides the bar would put unexplained "FREE GIFT" rows in front of them.

     This is keyed off the setting alone, deliberately, and not off the discount
     probe — so that what Liquid renders and what this file does can never
     disagree. Liquid cannot see the probe. */
  function stripGiftLines() {
    var list = tiers();
    if (!list.length) return Promise.resolve({ removed: 0, added: 0 });
    var giftVariants = {};
    list.forEach(function (t) { giftVariants[String(t.variant_id)] = true; });

    return getCart().then(function (cart) {
      var keys = cart.items.filter(function (item) {
        return (item.properties || {})[GIFT_PROP] || giftVariants[String(item.variant_id)];
      }).map(function (item) { return item.key; });
      if (!keys.length) return { removed: 0, added: 0 };

      state.reconciling = true;
      var chain = Promise.resolve();
      keys.forEach(function (key) {
        chain = chain.then(function () {
          return postJSON(routes().change, { id: key, quantity: 0 }).catch(function () {});
        });
      });
      return chain
        .then(function () { state.reconciling = false; return refresh(); })
        // Reported as 0 removed: `removed` drives the "a gift was taken away"
        // warning at checkout, and a gift withdrawn because the sale is on is
        // not something to stop the shopper over.
        .then(function () { return { removed: 0, added: 0 }; });
    }).catch(function (err) {
      state.reconciling = false;
      console.warn('[hn-cart] gift strip', err && err.message);
      return { removed: 0, added: 0 };
    });
  }

  /* Quantities first, gifts second. The ladder's thresholds read
     `final_line_price`, so it has to see the basket the shopper will actually
     be charged for — and a bump that the discount pays for leaves the
     subtotal unchanged, which is only true once the discount has landed. */
  /* Reconciles run one at a time.

     Adding to the cart schedules one of these, and the theme, the drawer and the
     checkout gate can each ask for another while it is still going. Two of them
     overlapping means two independent before/after readings of the same cart,
     and the second can approve a bump the first is halfway through undoing --
     which is how a basket that should have been left at four units was measured
     at six.

     The queued run re-reads the cart rather than trusting the snapshot it was
     handed, because by the time it starts that snapshot describes a cart that
     has already moved. */
  function reconcileAll(cartLike) {
    var queued = !!state.queue;
    var run = function () { return reconcileOnce(queued ? null : cartLike); };
    state.queue = (state.queue || Promise.resolve()).then(run, run);
    return state.queue;
  }

  function reconcileOnce(cartLike) {
    return reconcileB2G2(cartLike).then(function (b) {
      var gifts = b2g2Cfg().active ? stripGiftLines() : reconcileGifts();
      return gifts.then(function (g) {
        if (b.bumped) {
          notice(b2g2Cfg().notice ||
                 'Buy 2 Get 2 applied — your free units were added. You pay for half.');
        } else if (b.reverted) {
          notice(b2g2Cfg().expired_notice ||
                 'The Buy 2 Get 2 offer is not running right now, so your quantities were left as you set them.');
        }
        return {
          bumped: b.bumped || 0,
          reverted: !!b.reverted,
          removed: (g && g.removed) || 0,
          added: (g && g.added) || 0
        };
      });
    });
  }

  /* --------------------------------------------------------- gift tiers */

  /* Threshold maths must ignore the gift lines themselves, or adding a gift
     can push the subtotal over the next tier and the drawer starts handing
     out prizes for prizes. */
  function giftSubtotal(cart) {
    var giftVariants = {};
    tiers().forEach(function (t) { giftVariants[String(t.variant_id)] = true; });
    return cart.items.reduce(function (sum, item) {
      if ((item.properties || {})[GIFT_PROP]) return sum;
      if (giftVariants[String(item.variant_id)]) return sum;
      return sum + item.final_line_price;
    }, 0);
  }

  function tiers() {
    return (state.settings.tiers || []).filter(function (t) { return t.variant_id && t.threshold > 0; })
      .sort(function (a, b) { return a.threshold - b.threshold; });
  }

  function reconcileGifts(cartLike) {
    if (state.reconciling) return Promise.resolve({ removed: 0, added: 0 });
    var list = tiers();
    if (!list.length) return Promise.resolve({ removed: 0, added: 0 });

    return (cartLike && cartLike.items ? Promise.resolve(cartLike) : getCart()).then(function (cart) {
      var subtotal = giftSubtotal(cart);
      var earned = {};
      list.forEach(function (t) { if (subtotal >= t.threshold) earned[String(t.variant_id)] = t; });

      // Every variant configured as a gift, earned or not. A ₹0 gift variant is
      // orderable like anything else, so a line is treated as a gift by its
      // variant id and not only by the property we stamp on it — otherwise
      // somebody who added the free variant directly would keep it in a cart
      // that never reached the threshold.
      var giftVariants = {};
      list.forEach(function (t) { giftVariants[String(t.variant_id)] = true; });

      var present = {};
      var removals = [];
      cart.items.forEach(function (item) {
        var vid = String(item.variant_id);
        var tagged = !!(item.properties || {})[GIFT_PROP];
        if (!tagged && !giftVariants[vid]) return;
        if (!earned[vid] || present[vid]) {
          removals.push(item.key);          // tier lost, unearned, or a duplicate
        } else {
          present[vid] = true;
          if (item.quantity !== 1) removals.push({ key: item.key, quantity: 1 });
        }
      });

      var additions = Object.keys(earned).filter(function (vid) { return !present[vid]; })
        .map(function (vid) { return earned[vid]; });

      if (!removals.length && !additions.length) return { removed: 0, added: 0 };

      state.reconciling = true;
      var chain = Promise.resolve();

      removals.forEach(function (r) {
        chain = chain.then(function () {
          return postJSON(routes().change, typeof r === 'string' ? { id: r, quantity: 0 } : { id: r.key, quantity: r.quantity });
        });
      });

      if (additions.length) {
        chain = chain.then(function () {
          return postJSON(routes().add, {
            items: additions.map(function (t) {
              var props = {};
              props[GIFT_PROP] = String(t.threshold);
              return { id: t.variant_id, quantity: 1, properties: props };
            })
          });
        });
      }

      return chain
        .catch(function (err) { console.warn('[hn-cart] gift reconcile', err && err.message); })
        .then(function () {
          state.reconciling = false;
          return refresh();
        })
        .then(function () { return { removed: removals.length, added: additions.length }; });
    });
  }

  /* -------------------------------------------------------------- open/close */

  function markOpen(v) {
    var root = document.getElementById(ROOT_ID);
    if (!root) return;
    state.open = v;
    root.hidden = false;
    root.classList.toggle('is-open', v);
    root.setAttribute('aria-hidden', v ? 'false' : 'true');
    document.documentElement.classList.toggle('hn-cart-locked', v);
    if (v) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
  }

  /* `skipRefresh` exists for the add-to-cart path, which already has fresher
     markup on the way and would otherwise fire a second render fetch. */
  function open(opts) {
    if (state.open) return;
    state.lastFocus = document.activeElement;
    markOpen(true);
    var panel = $('.hn-panel');
    if (panel) {
      var target = panel.querySelector('.hn-iconbtn') || panel;
      window.requestAnimationFrame(function () { target.focus({ preventScroll: true }); });
    }
    if (!(opts && opts.skipRefresh)) refresh();
  }

  function close() {
    if (!state.open) return;
    markOpen(false);
    if (state.lastFocus && state.lastFocus.focus) state.lastFocus.focus({ preventScroll: true });
  }

  function trapFocus(e) {
    if (!state.open || e.key !== 'Tab') return;
    var panel = $('.hn-panel');
    if (!panel) return;
    var items = $$('a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])', panel)
      .filter(function (el) { return el.offsetParent !== null; });
    if (!items.length) return;
    var first = items[0];
    var last = items[items.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  }

  /* -------------------------------------------------------------- binding */

  /* Both cart roots get the same handlers.

     They used to go on the drawer alone, and `sections/hn-cart-page.liquid`
     renders the very same snippets outside it with no script of its own -- so
     on /cart the quantity steppers, the number box and the bin did nothing at
     all. Not slowly, not with an error: the click reached no listener. Every
     cart link is intercepted into the drawer, which is why it went unnoticed;
     the page is what a bookmark, a Google result or an abandoned-cart email
     lands on. Same seam that left the page's countdown frozen at 00:00. */
  function bind() {
    bindRoot(document.getElementById(ROOT_ID));
    bindRoot(document.getElementById(PAGE_ROOT_ID));
  }

  function bindRoot(root) {
    if (!root || root.dataset.hnBound === '1') return;
    root.dataset.hnBound = '1';

    root.addEventListener('click', function (e) {
      var el = e.target.closest('[data-hn]');
      if (!el) return;
      var action = el.getAttribute('data-hn');

      if (action === 'close' || action === 'scrim') { e.preventDefault(); close(); return; }

      /* A row can stand for two cart lines once the discount splits a variant,
         so quantity and removal are addressed to the variant rather than to the
         line the button happens to sit on. Writing to one half only was what
         made the product impossible to delete: the other half stayed behind and
         the completion rule filled it back up. */
      var line = el.closest('[data-hn-line]');
      var variantId = line && line.getAttribute('data-variant');

      if (action === 'qty') {
        e.preventDefault();
        var next = parseInt(el.getAttribute('data-value'), 10);
        if (isNaN(next) || next < 0) return;
        if (variantId && b2g2Cfg().active) {
          var box = line.querySelector('[data-hn-qty-input]');
          b2g2StepTo(variantId, next, box ? parseInt(box.value, 10) || 0 : 0);
          return;
        }
        var key = el.getAttribute('data-key');
        if (!key) return;
        mutate({ id: key, quantity: next });
        return;
      }

      if (action === 'remove') {
        e.preventDefault();
        if (variantId && b2g2Cfg().active) { b2g2SetVariantTotal(variantId, 0); return; }
        mutate({ id: el.getAttribute('data-key'), quantity: 0 });
        return;
      }

      if (action === 'b2g2-claim') {
        e.preventDefault();
        var target = parseInt(el.getAttribute('data-target'), 10);
        if (!variantId || isNaN(target) || target < 1) return;
        el.setAttribute('disabled', 'disabled');
        setBusy(true);
        b2g2Claim(variantId, target).then(function () { setBusy(false); });
        return;
      }

      if (action === 'add') {
        e.preventDefault();
        var id = parseInt(el.getAttribute('data-variant'), 10);
        if (!id) return;
        b2g2SetOptOut(id, false);
        state.lastAdded = String(id);
        el.setAttribute('disabled', 'disabled');
        setBusy(true);
        postJSON(routes().add, { items: [{ id: id, quantity: 1 }] })
          .then(function () { return refresh(); })
          .then(function () { return reconcileAll(); })
          .catch(function (err) { console.warn('[hn-cart] add', err && err.message); })
          .then(function () { setBusy(false); });
      }
    });

    root.addEventListener('change', function (e) {
      var input = e.target.closest('input[data-hn-qty-input]');
      if (!input) return;
      var next = parseInt(input.value, 10);
      if (isNaN(next) || next < 0) { refresh(); return; }
      var row = input.closest('[data-hn-line]');
      var vid = row && row.getAttribute('data-variant');
      if (vid && b2g2Cfg().active) {
        b2g2StepTo(vid, next, parseInt(input.defaultValue, 10) || 0);
        return;
      }
      mutate({ id: input.getAttribute('data-key'), quantity: next });
    });

    startTimers(root);
  }

  /* Countdown is per-visit rather than a fixed date: a promo that reads
     "expired" to every visitor for the rest of the campaign is worse than one
     that restarts. The deadline is kept in sessionStorage so a page change
     does not gift the shopper a fresh window. */
  function startTimers(root) {
    $$('[data-hn-countdown]', root).forEach(function (el) {
      if (el.dataset.hnTicking === '1') return;
      el.dataset.hnTicking = '1';
      var minutes = parseInt(el.getAttribute('data-hn-countdown'), 10) || 5;
      var storeKey = 'hn-countdown-' + (el.getAttribute('data-hn-countdown-key') || 'default');
      var ends = parseInt(window.sessionStorage.getItem(storeKey), 10);
      var now = Date.now();
      if (!ends || isNaN(ends) || ends < now) {
        ends = now + minutes * 60000;
        try { window.sessionStorage.setItem(storeKey, String(ends)); } catch (e) { /* private mode */ }
      }
      var mm = el.querySelector('[data-hn-min]');
      var ss = el.querySelector('[data-hn-sec]');
      var tick = function () {
        var left = ends - Date.now();
        if (left <= 0) {
          // A timer frozen at 00:00 advertises an offer that has visibly
          // expired, which is worse than no timer. Roll the window instead.
          ends = Date.now() + minutes * 60000;
          try { window.sessionStorage.setItem(storeKey, String(ends)); } catch (e) { /* private mode */ }
          left = ends - Date.now();
        }
        var m = Math.floor(left / 60000);
        var s = Math.floor((left % 60000) / 1000);
        if (mm) mm.textContent = String(m).padStart(2, '0');
        if (ss) ss.textContent = String(s).padStart(2, '0');
      };
      window.setInterval(tick, 1000);
      tick();
    });
  }

  /* --------------------------------------------- intercept cart writes */

  function afterWrite(url) {
    // Our own calls already render and reconcile; re-entering here would fire
    // a second render for every add and undo the round trip we just saved.
    if (state.reconciling || state.suppress > 0) return;
    var isAdd = /\/cart\/add/.test(url);
    window.setTimeout(function () {
      refresh().then(function () { return reconcileAll(); }).then(function () {
        if (isAdd && state.settings.open_on_add) open();
      });
    }, 60);
  }

  function patchNetwork() {
    var nativeFetch = window.fetch;
    if (typeof nativeFetch === 'function') {
      window.fetch = function (input, init) {
        var url = typeof input === 'string' ? input : (input && input.url) || '';
        var p = nativeFetch.apply(this, arguments);
        if (WRITE_RE.test(url)) p.then(function (res) { if (res && res.ok) afterWrite(url); }).catch(function () {});
        return p;
      };
    }
    var open_ = XMLHttpRequest.prototype.open;
    XMLHttpRequest.prototype.open = function (method, url) {
      this.__hnUrl = url;
      return open_.apply(this, arguments);
    };
    var send_ = XMLHttpRequest.prototype.send;
    XMLHttpRequest.prototype.send = function () {
      var xhr = this;
      xhr.addEventListener('load', function () {
        if (xhr.status >= 200 && xhr.status < 300 && WRITE_RE.test(xhr.__hnUrl || '')) afterWrite(xhr.__hnUrl);
      });
      return send_.apply(this, arguments);
    };
  }

  /* Add to cart must open this drawer, never navigate.

     Watching the network alone was not enough: the theme binds its own click
     handler to `.js_frm_cart`, and that handler decides where the shopper goes
     afterwards — on this store it can end up at /cart, which is exactly what
     the drawer exists to avoid. So the click is taken in the capture phase and
     stopped before the theme ever sees it.

     The request is built from the form's own FormData rather than a variant id
     we guess at, which keeps the selected variant, the quantity and any line
     item properties other apps have injected into that form. */
  function addFromForm(form, btn) {
    /* Open first, fetch second.

       The drawer used to appear only after add -> render -> cart -> render,
       three or four round trips deep, so on a phone the tap felt dead for a
       second or more. Now the panel slides in on the tap with the previous
       cart still in it and a busy state over the top, and `sections` on the
       add request means the new markup arrives with the add itself rather
       than in a follow-up fetch. The gift check runs after the shopper can
       already see the panel. */
    var fd = new FormData(form);
    // Adding the product again is an explicit fresh intention, so it lifts any
    // opt-out an earlier removal left behind.
    var addedId = fd.get('id');
    if (addedId) { b2g2SetOptOut(addedId, false); state.lastAdded = String(addedId); }
    fd.append('sections', SECTION_ID);
    fd.append('sections_url', window.location.pathname + window.location.search);

    setBusy(true);
    if (btn) btn.setAttribute('aria-busy', 'true');
    if (state.settings.open_on_add !== false) open({ skipRefresh: true });

    state.suppress++;
    return fetch(routes().add, { method: 'POST', body: fd, headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json().then(function (d) { if (!r.ok) throw new Error(d.description || r.statusText); return d; }); })
      .then(function (data) {
        if (!applySections(data.sections)) return refresh();
      })
      .then(function () { setBusy(false); return reconcileAll(); })
      .catch(function (err) {
        /* Sold out, or a variant the form did not carry.

           This used to `close(); form.submit();` — hand the browser a native
           POST to /cart/add, which lands the shopper on the full cart page.
           From the shopper's side that reads as the drawer sliding in and then
           straight back out while the page navigates away: the exact behaviour
           this drawer exists to replace, arriving only on the failures nobody
           hits while testing. A programmatic form.submit() also fires no submit
           event, so interceptForms() below never sees it and cannot stop it.

           The instruction from the shop owner is that adding to cart must never
           leave the page, so the drawer stays where it is.

           refresh() puts the true cart back on screen: the panel was opened
           optimistically with the pre-add contents in it and the add did not
           happen, so the numbers on screen were never written.

           A message naming the reason belongs here and is NOT shipped. Both
           notice() and a copy rendered from mm-atc.js were tried; on this
           button path neither ever reached the DOM, while the same call from
           the console did — so the failure is understood to be a redraw race
           that was not pinned down, and unproven code was not left behind
           pretending to inform anyone. A failed add is currently silent. */
        console.warn('[hn-cart] add', err && err.message);
        return refresh();
      })
      .then(function () {
        state.suppress--;
        setBusy(false);
        if (btn) btn.removeAttribute('aria-busy');
      });
  }

  function interceptAddButtons() {
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('button, input[type="submit"]');
      if (!btn || btn.disabled) return;
      var form = btn.closest('form[action*="/cart/add"]');
      if (!form || form.hasAttribute('data-hn-ignore')) return;
      // Buy now / dynamic checkout buttons belong to checkout, not the drawer.
      var name = btn.getAttribute('name') || '';
      if (name === 'checkout' || /shopify-payment-button|dynamic-checkout|buy[-_ ]?now/i.test(btn.className + ' ' + name)) return;
      if (btn.closest('.shopify-payment-button')) return;
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;

      e.preventDefault();
      e.stopImmediatePropagation();
      addFromForm(form, btn);
    }, true);
  }

  /* Plain (non-AJAX) add-to-cart forms would navigate away to /cart. Submit
     them through the API instead so the shopper stays on the page. */
  function interceptForms() {
    document.addEventListener('submit', function (e) {
      var form = e.target;
      if (!(form instanceof HTMLFormElement)) return;
      var action = form.getAttribute('action') || '';
      if (!/\/cart\/add/.test(action)) return;
      if (form.hasAttribute('data-hn-ignore')) return;
      e.preventDefault();
      setBusy(true);
      fetch(routes().add, { method: 'POST', body: new FormData(form), headers: { Accept: 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function () { return refresh(); })
        .then(function () { return reconcileAll(); })
        .catch(function (err) { console.warn('[hn-cart] form add', err && err.message); })
        .then(function () { setBusy(false); if (state.settings.open_on_add) open(); });
    }, true);
  }

  /* Anything that used to open the theme's own drawer, or link to /cart,
     opens this one instead. */
  function interceptOpeners() {
    document.addEventListener('click', function (e) {
      var el = e.target.closest('a[href$="/cart"], a[href*="/cart?"], [data-id="#nt_cart_canvas"], .icon_cart a, [data-hn-open]');
      if (!el) return;
      if (el.hasAttribute('data-hn-ignore')) return;
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
      if (state.settings.intercept_cart_links === false) return;
      e.preventDefault();
      e.stopPropagation();
      open();
    }, true);
  }


  /* Checkout has to go through Shopflo, not Shopify.

     The theme wires Shopflo up by selector — layout/theme.liquid registers
     `#icartMainContent`/`.icart-checkout-btn` and `.icart-pg`/`.icart-pg-checkout`,
     and Shopflo hides the native button and inserts its own in front of it.
     Neither selector exists in this drawer, so the native `name="checkout"`
     submit fell straight through to Shopify's checkout.

     Rather than register the drawer with that observer (which would inject a
     button carrying the old theme's classes into a panel styled nothing like
     it), the drawer's own button calls the same global the injected one does.
     If Shopflo has not loaded, or its checkout is switched off, the form
     submits as before — a shopper is never left with a dead button. */
  function shopfloReady() {
    if (typeof window.handleFloCheckoutBtn !== 'function') return false;
    var flo = window.Shopflo;
    if (flo && flo.isFloCheckoutEnabled === false) return false;
    return true;
  }

  function proceedToCheckout(form) {
    if (shopfloReady()) {
      try { window.handleFloCheckoutBtn(); return; }
      catch (err) { console.warn('[hn-cart] shopflo checkout', err && err.message); }
    }
    if (form) form.submit();
  }

  function notice(text) {
    /* The footer is only rendered when the cart has something in it, and the
       one moment a notice matters most is a failed add — which leaves the cart
       empty and the footer absent. Returning here put the shopper in front of
       an open drawer reading "your cart is empty" with nothing to say why the
       tap did nothing, so the panel itself is the fallback host. */
    var host = $('.hn-foot__inner') || $('.hn-panel');
    if (!host) return;
    var el = host.querySelector('[data-hn-notice]');
    if (!el) {
      el = document.createElement('p');
      el.setAttribute('data-hn-notice', '');
      el.className = 'hn-notice';
      host.insertBefore(el, host.firstChild);
    }
    el.textContent = text;
    window.clearTimeout(el._t);
    el._t = window.setTimeout(function () { if (el.parentNode) el.parentNode.removeChild(el); }, 6000);
  }

  /* Pack upgrade: swap a line's variant for a bigger pack of the same product.

     Bound to the DOCUMENT, not to the drawer root, and that is the whole reason
     this is a function of its own rather than another branch in bind(). The
     /cart page renders hn-cart-line outside `#hn-cart-drawer`, so a listener on
     the drawer root never sees a click on it -- the same seam that left the
     page's countdown frozen at 00:00 (see boot()). Written as a drawer branch
     first, the button on /cart rendered perfectly, disabled nothing, changed
     nothing, and logged nothing: the page's own quantity and remove buttons are
     dead there today for exactly this reason, and this one would have joined
     them.

     The bigger pack is ADDED FIRST and the old line removed only once that has
     come back 200. The other order means a failed add -- a variant that sold
     out between render and tap, a dropped connection -- leaves the shopper with
     an emptier cart than they started with, and what they lost is the thing
     they were told they were improving.

     Removal is addressed to the VARIANT, not to the line key the button sits
     beside: hn-cart-line folds every line of a variant into one row, so a key
     would leave the other half of a split line behind and the shopper would be
     charged for both packs. Gift lines are never touched -- the tier
     reconciler owns those.

     Redraw depends on where the click came from. Inside the drawer, the Section
     Rendering API redraw is enough. On /cart the section is a different one
     that refresh() does not fetch, so the page is reloaded: the alternative is
     a page still showing the old pack at the old price after a write it just
     made, which is worse than a flash. */
  function upgradeLine(el) {
    var toId = parseInt(el.getAttribute('data-variant'), 10);
    var fromId = String(el.getAttribute('data-old-variant') || '');
    var upQty = parseInt(el.getAttribute('data-qty'), 10) || 1;
    if (!toId || !fromId || fromId === String(toId)) return Promise.resolve(false);

    /* The button says what it is doing.

       Three round trips -- add, read, remove -- take two seconds on a good
       connection, and the first version showed nothing for all of them: the
       label stayed "Upgrade", greyed out, and the shopper's reading of that is
       that the tap failed. It is the one moment in this flow where they are
       being asked to spend more money, so it has to look deliberate. */
    var label = el.textContent;
    el.setAttribute('disabled', 'disabled');
    el.setAttribute('data-hn-working', '1');
    el.textContent = el.getAttribute('data-working-label') || 'Adding…';

    function restore() {
      el.removeAttribute('disabled');
      el.removeAttribute('data-hn-working');
      el.textContent = label;
    }

    b2g2SetOptOut(toId, false);
    state.lastAdded = String(toId);
    setBusy(true);

    /* The pack being replaced has to still be in the cart.

       This box was rendered at some earlier moment, and the cart can have moved
       since -- a second tab, a back button, a stale bfcache page. Adding the
       bigger pack and then finding nothing to remove leaves the shopper holding
       BOTH, which is the failure the add-first order exists to avoid, arriving
       by another door. So the cart is read BEFORE anything is written. */
    return getCart()
      .then(function (cart) {
        var stale = !(cart.items || []).some(function (it) {
          var gift = it.properties && it.properties[GIFT_PROP];
          return String(it.variant_id) === fromId && !gift;
        });
        if (stale) {
          restore();
          return refresh().then(function () { throw new Error('line already gone'); });
        }
        /* The new line is stamped as an accepted upgrade, and the snippet
           refuses to offer another for this product once it sees the stamp.
           A cart property rather than a cookie: it belongs to the cart, so it
           survives a reload and follows the cart to another device. The
           leading underscore keeps it off the checkout and the order. */
        return postJSON(routes().add, {
          items: [{ id: toId, quantity: upQty, properties: { _hn_upgraded: '1' } }]
        });
      })
      .then(function () { return getCart(); })
      .then(function (cart) {
        /* change.js one key at a time, not update.js with a map of them.

           `/cart/update.js` answers 200 to an `updates` object keyed by line
           item key and leaves the cart exactly as it was -- it wants variant
           ids -- so the shopper ended up holding the old pack AND the new one,
           with a success response and nothing in the console. Variant ids are
           not usable here either: the gift lines share a variant with nothing,
           but a shopper can hold the same variant as both a paid line and a
           gift, and a variant-keyed update cannot tell them apart. change.js
           addresses one line key, which is the only identifier that can. */
        var keys = [];
        (cart.items || []).forEach(function (it) {
          var gift = it.properties && it.properties._hn_gift_tier;
          if (String(it.variant_id) === fromId && !gift) keys.push(it.key);
        });
        var chain = Promise.resolve();
        keys.forEach(function (key) {
          chain = chain.then(function () {
            return postJSON(routes().change, { id: key, quantity: 0 });
          });
        });
        return chain;
      })
      .then(function () {
        /* No page reload, on either surface.

           The first version reloaded when the click came from outside the
           drawer, because refresh() only re-rendered the drawer's section.
           Measured on /cart that was ELEVEN SECONDS from tap to a usable page
           -- three cart calls, then a full document load carrying the whole
           theme and its ad tags -- with a dead grey button throughout. Now that
           refresh() re-renders whichever cart sections are on the page, both
           surfaces redraw in place in about two. */
        return refresh().then(function () { return reconcileAll(); });
      })
      .catch(function (err) {
        console.warn('[hn-cart] upgrade', err && err.message);
        restore();
        return refresh();
      })
      .then(function () { setBusy(false); return true; });
  }

  function interceptUpgrade() {
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-hn="upgrade"]');
      if (!btn || btn.disabled) return;
      e.preventDefault();
      e.stopPropagation();
      upgradeLine(btn);
    }, true);
  }

  /* Checkout re-checks the whole basket before handing off.

     A ₹0 gift product is orderable by anyone who has its variant id, and the
     periodic reconcile only runs on cart changes — so a cart could in principle
     reach the checkout button holding a gift it never earned. The same is true
     of quantities: a cart permalink, or a tab left open from before the sale
     started, can arrive here with a line the offer should have completed.

     This is the last gate before Shopflo takes over. Anything the reconcilers
     had to change is shown to the shopper rather than pushed silently into a
     checkout they are about to pay on; the next click goes straight through,
     because by then there is nothing left to change. */
  function interceptCheckout() {
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-hn="checkout"]');
      if (!btn) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      var form = btn.closest('form');
      setBusy(true);
      reconcileAll()
        .catch(function () { return { removed: 0, added: 0, bumped: 0, reverted: false }; })
        .then(function (res) {
          setBusy(false);
          if (res && res.removed) {
            open();
            notice(state.settings.gift_removed_notice ||
                   'A free gift was removed because your cart no longer qualifies. Check your total and continue.');
            return;
          }
          if (res && (res.bumped || res.reverted)) {
            open();   // reconcileAll has already posted its own notice
            return;
          }
          proceedToCheckout(form);
        });
    }, true);
  }

  /* ---------------------------------------------------------------- boot */

  function boot() {
    if (state.booted) return;
    state.booted = true;
    state.settings = readSettings();
    bind();
    // The /cart page renders the same blocks outside the drawer root, so its
    // countdown never reached startTimers() and sat frozen at 00:00.
    startTimers(document);
    patchNetwork();
    interceptAddButtons();
    interceptForms();
    interceptOpeners();

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') close();
      trapFocus(e);
    });

    interceptUpgrade();
    interceptCheckout();

    window.HNCart = {
      open: open, close: close, refresh: refresh,
      reconcile: reconcileAll, reconcileGifts: reconcileGifts, reconcileB2G2: reconcileB2G2,
      b2g2Config: b2g2Cfg, b2g2Target: b2g2Target, b2g2Claim: b2g2Claim,
      b2g2SetTotal: b2g2SetVariantTotal, b2g2OptOuts: b2g2OptOuts
    };

    // The cart may already qualify for a gift, or arrive from a permalink
    // holding a quantity the offer should have completed, when the page loads.
    reconcileAll();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
